import Decimal from 'decimal.js'
// Keep only recovery information, never login tokens or a cached financial outcome.
const namespace = 'abnormal-contract-request:v1:'
const keyPattern = /^[A-Za-z0-9_-]{16,64}$/
function prefix(scope) {
  if (!/^\d+:\d+$/.test(scope)) throw new Error('无法确认当前登录身份，合约扣除暂不可提交，请刷新页面')
  return namespace + scope + ':'
}
function valid(record) {
  return record && record.accountType === 'CONTRACT' && keyPattern.test(record.requestKey)
    && /^[1-9]\d{0,18}$/.test(record.userUid) && typeof record.coin === 'string' && record.coin.length > 0
    && /^\d+(\.\d+)?$/.test(record.amount)
    && (record.id == null || typeof record.id === 'string')
}
function readState(scope) {
  const value = window.localStorage.getItem(prefix(scope) + 'state')
  if (value === null) return { revision: 0, ended: {} }
  const state = JSON.parse(value)
  if (!state || !Number.isSafeInteger(state.revision) || state.revision < 0 || !state.ended
    || typeof state.ended !== 'object' || Array.isArray(state.ended)
    || Object.keys(state.ended).some(key => !keyPattern.test(key) || state.ended[key] !== true)) {
    throw new Error('合约请求保护记录异常，请核查原单，勿重新扣除')
  }
  return state
}
export function readRevision(scope) { return readState(scope).revision }
export function isRequestEnded(scope, requestKey) {
  if (!keyPattern.test(requestKey)) throw new Error('请求标识不正确')
  // Missing pending data is not proof of completion; only an explicit tombstone is.
  return readState(scope).ended[requestKey] === true
}
function writeChecked(name, value) {
  const text = JSON.stringify(value)
  window.localStorage.setItem(name, text)
  if (window.localStorage.getItem(name) !== text) throw new Error('无法保存合约请求，未发送扣除，请检查浏览器存储设置')
}
function advance(scope, state) {
  if (state.revision >= Number.MAX_SAFE_INTEGER) throw new Error('合约请求版本异常，请核查原单')
  state.revision++
  // Invalidate older balance queries/forms before changing pending records.
  writeChecked(prefix(scope) + 'state', state)
}
function locked(scope, action) {
  if (!window.navigator.locks) throw new Error('当前浏览器不支持合约请求保护，请使用新版浏览器并通过 HTTPS 访问')
  return window.navigator.locks.request(prefix(scope), action)
}
export function readRequests(scope) {
  const start = prefix(scope)
  const state = readState(scope)
  const records = []
  for (let i = 0; i < window.localStorage.length; i++) {
    const name = window.localStorage.key(i)
    if (!name || !name.startsWith(start) || name === start + 'state') continue
    const record = JSON.parse(window.localStorage.getItem(name))
    if (!valid(record) || name !== start + record.requestKey) throw new Error('合约待确认请求记录异常，请核查原单后处理，勿重新扣除')
    if (state.ended[record.requestKey] !== true) records.push(record)
  }
  return records
}
// new: register before POST; retry: explicitly handle an existing server operation;
// update: enrich a pending record only, never recreate a removed/finished request.
export async function saveRequest(scope, operation, mode = 'new', expectedRevision) {
  const start = prefix(scope)
  const record = {
    accountType: 'CONTRACT', requestKey: operation.requestKey,
    userUid: String(operation.userUid), coin: String(operation.coin || '').toUpperCase(),
    amount: typeof operation.amount === 'number' ? new Decimal(operation.amount).toFixed() : String(operation.amount),
    id: operation.id == null ? null : String(operation.id),
  }
  if (!valid(record)) throw new Error('原扣除请求信息不完整，无法安全保存，请核查原单')
  if (!['new', 'retry', 'update'].includes(mode)) throw new Error('合约请求保存方式不正确')
  return locked(scope, () => {
    const state = readState(scope)
    if (state.ended[record.requestKey] === true) {
      if (mode === 'update') return false
      throw new Error('原请求已结束，请刷新核查，勿重复扣除')
    }
    if (mode === 'new' && state.revision !== expectedRevision) throw new Error('其他标签页已处理合约扣除，请重新查询余额并确认')
    const records = readRequests(scope)
    const old = records.find(item => item.requestKey === record.requestKey)
    if (mode === 'update' && !old) return false
    if (mode === 'new' && records.length) throw new Error('存在待确认的合约扣除请求，请先核查原单')
    if (mode === 'retry' && !record.id) throw new Error('缺少原操作单号，不能重试')
    // The existing API may return a JSON number. Preserve the original input string;
    // this comparison is only for recovery display, never a new deduction payload.
    const sameAmount = !old || (typeof operation.amount === 'number'
      ? Number(old.amount) === operation.amount : new Decimal(old.amount).eq(record.amount))
    if (old && (old.userUid !== record.userUid || old.coin !== record.coin || !sameAmount
      || (old.id && record.id && old.id !== record.id))) throw new Error('原请求信息不一致，请核查原单')
    if (mode !== 'update') advance(scope, state)
    writeChecked(start + record.requestKey, { ...record, amount: old ? old.amount : record.amount, id: record.id || (old && old.id) || null })
    return true
  })
}
export async function removeRequest(scope, requestKey, terminal = true) {
  if (!keyPattern.test(requestKey)) throw new Error('请求标识不正确')
  const name = prefix(scope) + requestKey
  return locked(scope, () => {
    const state = readState(scope)
    // Keep terminal tombstones: delayed responses/retries must not resurrect an ended request.
    // A structured pre-creation refusal is not terminal; its key may be reused after a fresh query.
    if (state.ended[requestKey] !== true) {
      if (terminal) state.ended[requestKey] = true
      advance(scope, state)
    }
    window.localStorage.removeItem(name)
    if (window.localStorage.getItem(name) !== null) throw new Error('原请求记录尚未清理，请刷新核查')
  })
}
